import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

/**
 * Structured error data returned by authentication API endpoints
 */
export interface ApiError {
  error: string;
  message: string;
  details?: Array<{
    field: string;
    message: string;
  }>;
}

/**
 * Error class representing authentication API errors with HTTP status and structured error data
 */
export class AuthApiError extends Error {
  public status: number;

  public apiError: ApiError;

  constructor(status: number, apiError: ApiError) {
    super(apiError.message);
    this.status = status;
    this.apiError = apiError;
    this.name = 'AuthApiError';
  }
}

/**
 * User-facing message shown when the server rejects an unlink request because
 * it is the user's only remaining sign-in method (409 `LAST_IDENTITY`).
 *
 * Exported so the connected `LinkedProvidersList` toast and the server-side
 * fallback path can share a single source of truth.
 */
export const LAST_IDENTITY_ERROR_MESSAGE = 'This is your only sign-in method. Add another first.';

function extractErrorCode(error: FetchBaseQueryError): string | undefined {
  if (!error.data || typeof error.data !== 'object') return undefined;
  const data = error.data as Record<string, unknown>;
  // Backend error envelope carries both `error` (legacy) and `code` (canonical)
  // UPPER_SNAKE_CASE identifiers — accept either.
  if (typeof data.code === 'string' && data.code.length > 0) return data.code;
  if (typeof data.error === 'string' && data.error.length > 0) return data.error;
  return undefined;
}

/**
 * Map HTTP status codes to user-friendly error messages
 */
export function getErrorMessage(status: number | string): string {
  const statusCode = typeof status === 'string' ? parseInt(status, 10) : status;

  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input.',
    401: 'Invalid credentials. Please try again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This email is already registered.',
    422: 'The provided data is invalid.',
    429: 'Too many requests. Please try again later.',
    500: 'Server error. Please try again later.',
    502: 'Server is temporarily unavailable.',
    503: 'Service unavailable. Please try again later.',
  };

  return statusMessages[statusCode] || 'An unexpected error occurred';
}

/**
 * Transform RTK Query errors into AuthApiError instances for backward compatibility
 */
export function transformRTKError(
  error: FetchBaseQueryError | SerializedError | undefined,
): AuthApiError | Error {
  // Handle FetchBaseQueryError (API errors)
  if (error && 'status' in error) {
    const fetchError = error as FetchBaseQueryError;

    let apiError: ApiError;

    if (fetchError.data && typeof fetchError.data === 'object') {
      // Backend returns error in this format
      const errorData = fetchError.data as Record<string, unknown>;
      apiError = {
        error: (errorData.error as string) || 'API_ERROR',
        message: (errorData.message as string) || 'An error occurred',
        details: errorData.details as Array<{ field: string; message: string }> | undefined,
      };
    } else {
      // Fallback for unexpected error format
      const status = typeof fetchError.status === 'number' ? fetchError.status : 500;
      apiError = {
        error: 'UNKNOWN_ERROR',
        message: getErrorMessage(status),
        details: undefined,
      };
    }

    const status = typeof fetchError.status === 'number' ? fetchError.status : 500;
    return new AuthApiError(status, apiError);
  }

  // Handle SerializedError (network/parsing errors)
  if (error && 'message' in error) {
    const serializedError = error as SerializedError;
    return new Error(serializedError.message || 'An unexpected error occurred');
  }

  // Fallback
  return new Error('An unexpected error occurred');
}

/**
 * Extract error message from any RTK Query error
 */
export function extractErrorMessage(
  error: FetchBaseQueryError | SerializedError | undefined,
  fallback = 'An unexpected error occurred',
): string {
  if (!error) {
    return fallback;
  }

  // Handle FetchBaseQueryError
  if ('status' in error) {
    const fetchError = error as FetchBaseQueryError;

    // Prefer a code-specific message when we recognise the backend error code —
    // e.g. 409 LAST_IDENTITY should never surface the generic "email already
    // registered" copy even though it shares the status code.
    if (fetchError.status === 409 && extractErrorCode(fetchError) === 'LAST_IDENTITY') {
      return LAST_IDENTITY_ERROR_MESSAGE;
    }

    if (fetchError.data && typeof fetchError.data === 'object') {
      const errorData = fetchError.data as Record<string, unknown>;
      if (typeof errorData.message === 'string') {
        return errorData.message;
      }
    }

    const status = typeof fetchError.status === 'number' ? fetchError.status : 500;
    return getErrorMessage(status);
  }

  // Handle SerializedError
  if ('message' in error) {
    const serializedError = error as SerializedError;
    return serializedError.message || fallback;
  }

  return fallback;
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthenticationError(
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean {
  if (!error || !('status' in error)) {
    return false;
  }

  const fetchError = error as FetchBaseQueryError;
  const status = fetchError.status;

  // Check for numeric 401 status
  if (typeof status === 'number') {
    return status === 401;
  }

  // Check for string '401' status (though this is unlikely in practice)
  return false;
}

/**
 * Check if error is a re-authentication required error (403 SESSION_TOO_OLD / SESSION_REQUIRED).
 *
 * Backend returns these codes when a sensitive action (e.g. unlinking an OAuth provider)
 * requires a freshly-minted session. The UI should prompt the user to re-sign-in and
 * preserve the intended action.
 */
export function isReAuthRequiredError(
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean {
  if (!error || !('status' in error)) {
    return false;
  }

  const fetchError = error as FetchBaseQueryError;

  // Backend only ever returns these codes with a 403. Requiring the status
  // code prevents a non-403 response body from accidentally triggering the
  // re-auth modal (e.g. a 200 from a different endpoint that happens to carry
  // the same `error` string).
  if (fetchError.status !== 403) {
    return false;
  }

  if (!fetchError.data || typeof fetchError.data !== 'object') {
    return false;
  }

  // Backend error envelope carries both `error` (legacy) and `code` (canonical)
  // UPPER_SNAKE_CASE identifiers — accept either so a future rename of one
  // field doesn't silently break the modal trigger.
  const errorData = fetchError.data as Record<string, unknown>;
  const code =
    typeof errorData.error === 'string'
      ? errorData.error
      : typeof errorData.code === 'string'
        ? errorData.code
        : undefined;

  return code === 'SESSION_TOO_OLD' || code === 'SESSION_REQUIRED';
}

/**
 * Check if error indicates the connected Apple Music session has expired or
 * is no longer authorized on a call made after the initial connect (WEB-446).
 *
 * Forward-wired: no live LOCI endpoint emits `APPLE_MUSIC_TOKEN_EXPIRED` /
 * `APPLE_MUSIC_NOT_AUTHORIZED` today — the `/v1/music/connect` storefront
 * probe collapses every Apple 401/403 into a single `INVALID_MUSIC_TOKEN`
 * (see `services/backend-api/src/routes/versions/v1/music/index.ts`), and no
 * endpoint re-validates a stored connection's token on a later call yet. This
 * detector activates automatically once such an endpoint starts emitting
 * these codes — no frontend change needed then.
 */
export function isAppleMusicTokenExpiredError(
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean {
  if (!error || !('status' in error)) {
    return false;
  }

  const fetchError = error as FetchBaseQueryError;
  if (fetchError.status !== 401 && fetchError.status !== 403) {
    return false;
  }

  const code = extractErrorCode(fetchError);
  return code === 'APPLE_MUSIC_TOKEN_EXPIRED' || code === 'APPLE_MUSIC_NOT_AUTHORIZED';
}

/**
 * Check if error indicates the connected Apple ID has no active Apple Music
 * subscription (WEB-446).
 *
 * Forward-wired — see {@link isAppleMusicTokenExpiredError} doc for why no
 * live endpoint emits `APPLE_MUSIC_NOT_SUBSCRIBED` yet.
 */
export function isAppleMusicNotSubscribedError(
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean {
  if (!error || !('status' in error)) {
    return false;
  }

  return extractErrorCode(error as FetchBaseQueryError) === 'APPLE_MUSIC_NOT_SUBSCRIBED';
}

/**
 * Check if the error is a 409 `LAST_IDENTITY` response.
 *
 * The backend returns this code from `DELETE /api/v1/auth/oauth/:provider`
 * (and the passkey unlink equivalent) when removing the identity would leave
 * the user with no way to sign in. The UI should pre-empt the call with a
 * disabled button + tooltip, and fall back to a toast for the rare
 * cross-tab race that still reaches the server.
 */
export function isLastIdentityError(
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean {
  if (!error || !('status' in error)) {
    return false;
  }

  const fetchError = error as FetchBaseQueryError;
  if (fetchError.status !== 409) {
    return false;
  }

  return extractErrorCode(fetchError) === 'LAST_IDENTITY';
}

/**
 * Check if a failed registration attempt means the email is already registered.
 *
 * The `/register` endpoint (`services/backend-api/src/routes/versions/v1/auth.ts`)
 * returns HTTP **409** *exclusively* for a duplicate email — nothing else on that
 * route uses 409 (rate limiting is 429, validation is 400/422) — so the status
 * alone is an unambiguous signal. We deliberately do **not** key on an error
 * code string: the backend body carries the raw `error: 'User already exists'`,
 * not a canonical UPPER_SNAKE_CASE code, and the `createAuthErrorHandler` mapping
 * that would produce `EMAIL_EXISTS` is not in the RTK register path. The
 * `EMAIL_EXISTS` / `EMAIL_ALREADY_EXISTS` code check below is forward-compatible
 * defense-in-depth for a future backend that emits a real code; it never fires
 * today but is harmless.
 *
 * Operates on the post-transform {@link AuthApiError} the signup page holds
 * (see `AuthContextRTK.register`), not the raw `FetchBaseQueryError` the other
 * predicates in this module take.
 */
export function isEmailAlreadyRegisteredError(error: unknown): error is AuthApiError {
  if (!(error instanceof AuthApiError)) {
    return false;
  }

  if (error.status === 409) {
    return true;
  }

  const code = error.apiError.error;
  return code === 'EMAIL_EXISTS' || code === 'EMAIL_ALREADY_EXISTS';
}

/**
 * Check if error is a validation error (422 or 400 with details)
 */
export function isValidationError(
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean {
  if (!error || !('status' in error)) {
    return false;
  }

  const fetchError = error as FetchBaseQueryError;
  const status = fetchError.status;

  // Only check numeric status codes
  if (typeof status !== 'number') {
    return false;
  }

  // Check for 422 status
  if (status === 422) {
    return true;
  }

  // Check for 400 with details array
  if (status === 400) {
    if (fetchError.data && typeof fetchError.data === 'object') {
      const errorData = fetchError.data as Record<string, unknown>;
      return Array.isArray(errorData.details) && errorData.details.length > 0;
    }
  }

  return false;
}

/**
 * Extract field-level validation errors for React Hook Form
 */
export function extractValidationErrors(
  error: FetchBaseQueryError | SerializedError | undefined,
): Record<string, string> {
  if (!error || !('status' in error)) {
    return {};
  }

  const fetchError = error as FetchBaseQueryError;

  if (!fetchError.data || typeof fetchError.data !== 'object') {
    return {};
  }

  const errorData = fetchError.data as Record<string, unknown>;

  if (!Array.isArray(errorData.details)) {
    return {};
  }

  const validationErrors: Record<string, string> = {};

  for (const detail of errorData.details) {
    if (
      detail &&
      typeof detail === 'object' &&
      'field' in detail &&
      'message' in detail &&
      typeof detail.field === 'string' &&
      typeof detail.message === 'string'
    ) {
      validationErrors[detail.field] = detail.message;
    }
  }

  return validationErrors;
}
